import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBackLayer } from "../backstack";
import { HIDDEN, PageShown } from "../pageShown";
import { ShellIcon, type ShellIconName } from "./ShellIcons";

/*
 * The pages' shared parts (kit.css): the prototype's core components (v2.20) under the same names and
 * the same props, so a page is moved over line by line and a meaning looks the same on every page.
 * Prototype → here: Page, PageHead, Card, SectionHead, Pill, Chip, Btn, Seg (label and icon in front),
 * Tabs, Switch, Empty, IconBox (.ico-b), Drawer, toast, Amt (and Count.tsx for Roll). Icons are ShellIcons' names.
 */

type Tone = "good" | "bad" | "warn" | "ai" | "msg" | "gold" | "brand";
const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

/** The reader asked for less motion: everything is drawn where it ends. */
export const stillWanted = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/*
 * A page coming on screen (prototype core.js Page, v2.20): its cards rise one after another, and for 2.6 s it is
 * `.play`, which lets each page's bars grow, lines draw and cells pop (kit.css and the pages' own sheets). A page
 * here is not mounted again when it comes back — it waits, laid out, off screen (styles.css .viewport.hidden) — so
 * this runs each time it is shown, not once. The cards are animated from here, not by a class: a page kept off
 * screen is not restyled, so a class put back on it would not start its animation again.
 */
const RISE: Keyframe[] = [
  { opacity: 0, transform: "translateY(10px)" },
  { opacity: 1, transform: "none" },
];
/**
 * The first twelve cards, 40 ms apart, as the prototype's GSAP did (.35 s, power2.out); later ones are below the fold.
 * A tab kept but not open (经营's) has its cards too: they are skipped. Nothing here asks the browser for a style or a
 * size — that made it work out the whole page coming on screen inside this call (15–60 ms, measured).
 */
export function riseIn(root: Element | null) {
  if (!root || stillWanted()) return;
  let i = 0;
  for (const card of root.querySelectorAll<HTMLElement>(".lz-card, [data-rise]")) {
    if (card.closest("[hidden]")) continue;
    card.animate(RISE, { duration: 350, delay: i * 40, easing: "cubic-bezier(.33, 1, .68, 1)", fill: "backwards" });
    if (++i === 12) break;
  }
}
/**
 * What a page (or a tab kept mounted, 经营's) does when it comes on screen — or when it is first drawn, after its
 * first read: its cards rise, and it says `play` for 2.6 s. Its root takes the ref this returns. `shown` is the
 * page's own (PageShown) unless a tab passes whether it is the one open. `rise="again"`: a page whose cards rise by
 * their own animation when drawn (小爱's panes, xa.css xa-rise) has them risen here only when it comes back.
 */
export function usePlay<T extends HTMLElement>(shown?: boolean, rise: boolean | "again" = true): [(el: T | null) => void, boolean] {
  const pageShown = useContext(PageShown);
  const on = shown ?? pageShown;
  const [el, setEl] = useState<T | null>(null);
  const [play, setPlay] = useState(false);
  const [was, setWas] = useState<[T | null, boolean]>([null, false]);
  if (was[0] !== el || was[1] !== on) {
    setWas([el, on]);
    if (on && el && (was[0] !== el || !was[1]) && !stillWanted()) setPlay(true);
  }
  useEffect(() => {
    if (!play) return;
    const t = window.setTimeout(() => setPlay(false), 2600);
    return () => window.clearTimeout(t);
  }, [play]);
  const drawn = useRef<T | null>(null);
  useLayoutEffect(() => {
    if (!on || !el) return;
    const first = drawn.current !== el;
    drawn.current = el;
    if (rise === true || (rise === "again" && !first)) riseIn(el);
  }, [on, el, rise]);
  return [setEl, play];
}

/** A page: it scrolls inside its cell, its content at most 1380 wide. `rise`: as usePlay's. */
export function Page({ className, rise = true, children }: { className?: string; rise?: boolean | "again"; children: ReactNode }) {
  const [ref, play] = usePlay<HTMLDivElement>(undefined, rise);
  return (
    <div className="lz-page" ref={ref}>
      <div className={cx("lz", play && "play", className)}>{children}</div>
    </div>
  );
}

/** A page's name, the one line that says what it answers, its controls on the right. */
export function PageHead({ title, answer, right }: { title: ReactNode; answer?: ReactNode; right?: ReactNode }) {
  return (
    <div className="lz-head">
      <div style={{ minWidth: 0 }}>
        <h1>{title}</h1>
        {answer && <div className="lz-head-a">{answer}</div>}
      </div>
      {right && <div className="lz-head-r">{right}</div>}
    </div>
  );
}

export function Card({
  tone,
  pad,
  flat,
  kpi,
  className,
  onClick,
  style,
  children,
}: {
  tone?: "win" | "loss" | "ai" | "gold" | "brand";
  /** 0: the card draws its own insides edge to edge. */
  pad?: 0;
  flat?: boolean;
  /** A key figure's card (prototype .card.kpi): under a pointer it lifts, its edge turns jade and a light runs along its top. */
  kpi?: boolean;
  className?: string;
  onClick?: () => void;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <section className={cx("lz-card", tone, pad === 0 && "pad0", flat && "flat", kpi && "kpi", onClick && "click", className)} onClick={onClick} style={style}>
      {children}
    </section>
  );
}

/** An icon in a tinted square (.ico-b): what a card or a row is about. */
export function IconBox({ icon, tone, size = 34 }: { icon: ShellIconName; tone?: Tone | "win" | "loss"; size?: number }) {
  return (
    <span className={cx("lz-ib", tone)} style={size === 34 ? undefined : { width: size, height: size, borderRadius: Math.round(size / 3) }}>
      <ShellIcon name={icon} size={Math.round(size * 0.47)} />
    </span>
  );
}

export function SectionHead({ title, sub, right, icon, tone }: { title: ReactNode; sub?: ReactNode; right?: ReactNode; icon?: ShellIconName; tone?: Tone | "win" | "loss" }) {
  return (
    <div className="lz-card-h">
      <h3>
        {icon && <IconBox icon={icon} tone={tone} size={28} />}
        {title}
      </h3>
      {sub && <span className="sub">{sub}</span>}
      {right && <div className="right">{right}</div>}
    </div>
  );
}

/** A state in a word. `live` breathes: the figure is being kept up to date. */
export function Pill({ tone, icon, live, big, title, className, children }: { tone?: Tone; icon?: ShellIconName; live?: boolean; big?: boolean; title?: string; className?: string; children: ReactNode }) {
  return (
    <span className={cx("lz-pill", tone, big && "big", className)} title={title}>
      {live && <i className="lz-live" aria-hidden="true" />}
      {icon && <ShellIcon name={icon} />}
      {children}
    </span>
  );
}

/** A job in a word, filled: 急 / 今天 / 订单 / 售后. */
export function Chip({ t, children }: { t?: "urgent" | "today" | "order" | "after"; children: ReactNode }) {
  return <span className={cx("lz-chip", t)}>{children}</span>;
}

export function Btn({
  kind,
  size,
  icon,
  title,
  disabled,
  onClick,
  children,
}: {
  kind?: "primary" | "soft" | "ghost" | "danger" | "gold";
  size?: "sm" | "lg";
  icon?: ShellIconName;
  title?: string;
  disabled?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}) {
  return (
    <button type="button" className={cx("lz-btn", kind, size, !children && "icon")} title={title} aria-label={children ? undefined : title} disabled={disabled} onClick={onClick}>
      {icon && <ShellIcon name={icon} />}
      {children}
    </button>
  );
}

/*
 * The picked choice's raised well (Seg) or underline (Tabs) is one thing that slides to the next pick (prototype core.js
 * useSlider: GSAP Flip, .32 s, power3.out). Put first in any .lz-seg or .lz-tabs, it finds the picked one (.on,
 * aria-checked, aria-selected) and goes there. It measures only after the browser laid the row out (ResizeObserver)
 * or after React wrote a new pick (MutationObserver): it never makes a page coming on screen lay itself out early.
 * Until it has been put in place once, the picked button draws its own well (shell.css), so nothing blinks.
 */
export function SegInd({ line }: { line?: boolean }) {
  const ref = useRef<HTMLElement>(null);
  // A page kept off screen is not laid out: a count changing there waits until the page is back.
  const shown = useContext(PageShown);
  const shownNow = useRef(shown);
  shownNow.current = shown;
  const stale = useRef(false);
  const again = useRef<(slide: boolean) => void>(() => undefined);
  useEffect(() => {
    if (shown && stale.current) {
      stale.current = false;
      again.current(false);
    }
  }, [shown]);
  // Everything below measures in the ResizeObserver's turn, which comes once the browser has laid the page out:
  // measured in the effect above or in the MutationObserver's microtask, a page coming back on screen was laid out
  // there and then — 50–150 ms of style and layout inside the press that showed 对话 or 客户 (10/27 trace, Kit.tsx place).
  useEffect(() => {
    const ind = ref.current;
    const box = ind?.parentElement;
    if (!ind || !box) return;
    let ready = 0;
    const place = (slide: boolean) => {
      if (!shownNow.current) {
        stale.current = true;
        return;
      }
      const on = box.querySelector<HTMLElement>(":scope > .on, :scope > [aria-checked='true'], :scope > [aria-selected='true']");
      if (!box.offsetWidth) return;
      ind.style.opacity = on ? "" : "0";
      if (!on) return;
      if (!slide) ind.style.transition = "none";
      if (line) {
        ind.style.transform = `translateX(${on.offsetLeft + 12}px)`;
        ind.style.width = `${Math.max(0, on.offsetWidth - 24)}px`;
      } else {
        ind.style.transform = `translate(${on.offsetLeft}px, ${on.offsetTop}px)`;
        ind.style.width = `${on.offsetWidth}px`;
        ind.style.height = `${on.offsetHeight}px`;
      }
      if (!slide || !ready)
        ready = window.requestAnimationFrame(() => {
          ind.style.transition = "";
          box.dataset.ind = "";
        });
    };
    // Whether the next placing slides; asking again re-observes the row, which the observer then reports after layout.
    let slideNext = false;
    const ro = new ResizeObserver(() => {
      const slide = slideNext;
      slideNext = false;
      place(slide);
    });
    const ask = (slide: boolean) => {
      slideNext = slideNext || slide;
      ro.unobserve(box);
      ro.observe(box);
    };
    again.current = ask;
    ro.observe(box);
    const mo = new MutationObserver(() => ask(!!ready));
    mo.observe(box, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["class", "aria-checked", "aria-selected"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.cancelAnimationFrame(ready);
      delete box.dataset.ind;
    };
  }, [line]);
  return <i ref={ref} className={cx("lz-seg-ind", line && "line")} aria-hidden="true" />;
}

/** A choice of a few, each [value, words, count?]; with `label`, an icon and a small label in front so two side by side never read as one. */
export function Seg<V extends string>({
  options,
  value,
  onChange,
  size,
  label,
  icon,
  className,
}: {
  options: ReadonlyArray<readonly [V, ReactNode, (number | null)?]>;
  value: V;
  onChange: (v: V) => void;
  /** sm: shell.css's own size (28); the prototype's full size (32) otherwise. */
  size?: "sm";
  label?: string;
  icon?: ShellIconName;
  className?: string;
}) {
  const seg = (
    <div className={cx("lz-seg", size !== "sm" && "md", !label && className)} role="radiogroup" aria-label={label}>
      <SegInd />
      {options.map(([v, words, n]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? "on" : ""} onClick={() => onChange(v)}>
          {words}
          {n != null && <span className="n">{n}</span>}
        </button>
      ))}
    </div>
  );
  if (!label) return seg;
  return (
    <div className={cx("lz-segl", className)}>
      <span className="lz-segl-k">
        {icon && <ShellIcon name={icon} size={14} />}
        {label}
      </span>
      {seg}
    </div>
  );
}

/** Tabs, underlined; each [value, words, count?, hot?] — a hot count is red. */
export function Tabs<V extends string>({ options, value, onChange }: { options: ReadonlyArray<readonly [V, ReactNode, (number | null)?, boolean?]>; value: V; onChange: (v: V) => void }) {
  return (
    <div className="lz-tabs" role="tablist">
      <SegInd line />
      {options.map(([v, words, n, hot]) => (
        <button key={v} type="button" role="tab" aria-selected={value === v} className={value === v ? "on" : ""} onClick={() => onChange(v)}>
          {words}
          {n != null && <span className={cx("n", hot && "hot")}>{n}</span>}
        </button>
      ))}
    </div>
  );
}

/** On or off; `ai` for 小爱's own switches. */
export function Switch({ on, onChange, label, ai, disabled }: { on: boolean; onChange: (on: boolean) => void; label: string; ai?: boolean; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} title={label} disabled={disabled} className={cx("lz-sw", on && "on", ai && "ai")} onClick={() => onChange(!on)} />
  );
}

/** Nothing to show, said in one line, and what to do about it. */
export function Empty({ icon = "check", line, action }: { icon?: ShellIconName; line: ReactNode; action?: ReactNode }) {
  return (
    <div className="lz-empty">
      <IconBox icon={icon} tone="win" size={44} />
      <b>{line}</b>
      {action}
    </div>
  );
}

/** The drawers (and what opens over one, 广告足迹) up now, the newest last, each saying whether its page is on screen. */
const slots: Array<{ shown: boolean }> = [];

/**
 * A place on top of the open drawers while mounted. The function says whether this one is the top of those on
 * screen: Esc is for that one only (an order over its customer's page closes alone), and never for one whose
 * page is off screen (pageShown.ts).
 */
export function useDrawerSlot(): () => boolean {
  const shown = useContext(PageShown);
  const [me] = useState(() => ({ shown }));
  me.shown = shown;
  useEffect(() => {
    slots.push(me);
    return () => {
      slots.splice(slots.indexOf(me), 1);
    };
  }, [me]);
  return useCallback(() => slots.filter((s) => s.shown).pop() === me, [me]);
}

/**
 * The prototype's drawer (core.js Drawer): a page of its own from the right, the whole screen on a phone. One for
 * every page; 客户表, 小爱 and the admin pages each had a copy, and 小爱's did not close on a phone's back. Drawn on
 * <body>, so nothing on the page (a container, a scroller, the sidebar sliding it) holds it in. Esc (the top one
 * only), the shade, ✕ and the phone's back close it, unless it is saving. Off screen with its page it hides and
 * keeps its place (pageShown.ts).
 */
export function Drawer({
  title,
  sub,
  lead,
  actions,
  foot,
  wide,
  busy,
  label,
  className,
  onClose,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  /** Before the title: the 「?」 of a case. */
  lead?: ReactNode;
  /** Beside ✕: 打开对话. */
  actions?: ReactNode;
  /** Along the bottom, never scrolled away: 取消 and 存. */
  foot?: ReactNode;
  wide?: boolean;
  /** Saving: nothing closes it until the answer is in. */
  busy?: boolean;
  /** What a screen reader calls it, when the title is not words. */
  label?: string;
  /** A page's own name for what it draws inside (ak-sheet). */
  className?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const shown = useContext(PageShown);
  const close = useRef(onClose);
  close.current = onClose;
  const held = useRef(!!busy);
  held.current = !!busy;
  const shut = useCallback(() => {
    if (!held.current) close.current();
  }, []);
  useBackLayer(true, shut);
  const onTop = useDrawerSlot();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onTop() && shut();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onTop, shut]);
  const hide = shown ? undefined : HIDDEN;
  return createPortal(
    <>
      <div className="lz-scrim" style={hide} onClick={shut} />
      <aside
        className={cx("lz-drawer", wide && "wide", className)}
        style={hide}
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === "string" ? title : undefined)}
      >
        <div className="lz-drawer-h">
          {lead}
          <div className="lz-drawer-t">
            <div className="lz-drawer-tt">{title}</div>
            {sub && <div className="lz-drawer-sub">{sub}</div>}
          </div>
          {actions}
          <Btn kind="ghost" icon="x" title="关掉" disabled={busy} onClick={shut} />
        </div>
        <div className="lz-drawer-b">{children}</div>
        {foot && <div className="lz-drawer-f">{foot}</div>}
      </aside>
    </>,
    document.body,
  );
}

/**
 * A line at the top of the screen for a moment (prototype core.js toast): 已复制…, 发给 5 个客户了, 刚刚更新. Drawn
 * straight into <body>, so any page — and anything after it has gone — can say one. `gold` for a win worth a moment
 * (开单了, 今天跟完了).
 */
export function toast(text: string, tone?: "win" | "bad" | "gold") {
  let box = document.getElementById("lz-toasts");
  if (!box) {
    box = document.createElement("div");
    box.id = "lz-toasts";
    box.className = "lz-toasts";
    box.setAttribute("role", "status");
    box.setAttribute("aria-live", "polite");
    document.body.appendChild(box);
  }
  const t = document.createElement("div");
  t.className = cx("lz-toast", tone);
  t.textContent = text;
  box.appendChild(t);
  window.setTimeout(() => {
    t.classList.add("out");
    window.setTimeout(() => t.remove(), 300);
  }, 2600);
}

/** An amount split for show: the sign, RM (or SGD) and the figure (prototype core.js splitMoney). */
const MONEY = /^([+−-]?)(RM|SGD ?)([\d.,]+)(.*)$/;
export function splitMoney(text: string): { sign: string; cur: string; n: string; tail: string } | null {
  const m = MONEY.exec(text);
  return m ? { sign: m[1] ?? "", cur: (m[2] ?? "").trim(), n: m[3] ?? "", tail: m[4] ?? "" } : null;
}

/** A big amount that does not count up, its RM a small grey prefix (prototype core.js Amt). */
export function Amt({ text, className }: { text: string; className?: string }) {
  const p = splitMoney(text);
  return (
    <span className={cx("lz-amt lz-n", className)} aria-label={text}>
      {p ? (
        <>
          {p.sign}
          <span className="lz-cur" aria-hidden="true">
            {p.cur}
          </span>
          <span aria-hidden="true">
            {p.n}
            {p.tail}
          </span>
        </>
      ) : (
        text
      )}
    </span>
  );
}
